import {
  socialExportGetYoutubePlaylistsQueryKey,
  useSocialExportGetYoutubeCapabilities,
  useSocialExportGetYoutubePlaylists,
  useSocialExportRefreshYoutubePlaylists,
} from '@mi-front/api-v2';
import { Box } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

type Props = {
  connectionId: string;
  isLoading: boolean;
  onRefreshStateChange?: (isRefreshing: boolean) => void;
};

/**
 * Refresh link for YouTube playlists on a connection.
 * Shows default helper text when the refresh link is hidden.
 */
export function YoutubePlaylistsRefreshLink({
  connectionId,
  isLoading,
  onRefreshStateChange,
}: Props) {
  const queryClient = useQueryClient();
  const { $t } = useIntl();
  const [refreshLinkVisible, setRefreshLinkVisible] = useState(true);

  const refreshMutation = useSocialExportRefreshYoutubePlaylists({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: socialExportGetYoutubePlaylistsQueryKey(connectionId),
        });
      },
    },
  });

  useEffect(() => {
    if (onRefreshStateChange) {
      onRefreshStateChange(refreshMutation.isPending);
    }
  }, [refreshMutation.isPending, onRefreshStateChange]);

  const handleRefresh = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isLoading || !connectionId) return;
    setRefreshLinkVisible(false);
    refreshMutation.mutate({ connectionId });
  };

  return (
    <Box component="span">
      {refreshLinkVisible && !isLoading && connectionId ? (
        <>
          {$t({
            id: 'socialExport.youtube.playlist.notFound',
            defaultMessage: 'Playlist no encontrada?',
          })}{' '}
          <Box
            component="span"
            onClick={handleRefresh}
            sx={{
              cursor: 'pointer',
              color: 'primary.main',
              textDecoration: 'underline',
              '&:hover': {
                textDecoration: 'none',
              },
            }}
          >
            {$t({
              id: 'socialExport.youtube.playlist.refresh',
              defaultMessage: 'Actualizar listado',
            })}
          </Box>
        </>
      ) : (
        <>
          {$t({
            id: 'socialExport.youtube.playlist.helperText',
            defaultMessage: 'Selecciona una o más playlists',
          })}
        </>
      )}
    </Box>
  );
}
